import { useState } from 'react';
import classNames from 'classnames';
import HelpIcon from '@mui/icons-material/Help';
import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { HELP_ROUTE } from 'components/help/constants';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import Toolbar from 'components/common/Toolbar/Toolbar';

import './GraphStyleMenu.scss';

export type GraphStyleSection = {
  style: string;
  itemContent: JSX.Element;
  settings?: JSX.Element | null;
  testId: string;
};

type GraphStyleMenuProps = {
  route: string;
  graphStyleParam: string;
  sections: Array<GraphStyleSection>;
  onSubmit: (graphStyle: string) => void;
};

const helpIcon = <HelpIcon fontSize="small" />;
/**
 * Component for selecting graph style as well as display settings.
 * This component is designed to be used as the dropdown body content for GraphStyleDropdown
 *
 * @prop route            current page url route
 * @prop graphStyleParam  graphStyle url param value
 * @prop sections         List of graph style sections with associated settings menus
 * @prop onSubmit         Callback to fire when submitting the menu.
 */

function GraphStyleMenu({ route, graphStyleParam, sections, onSubmit }: GraphStyleMenuProps) {
  const [currentStyle, setCurrentStyle] = useState(graphStyleParam || sections?.[0]?.style || '');

  const getGraphStyleList = () =>
    sections.map((section, idx) => {
      const graphStyleItemClass = classNames('GraphStyleMenu__StyleItem', {
        'GraphStyleMenu__StyleItem--selected': section.style === currentStyle,
      });
      return (
        <li
          key={`StyleItem-${section.style}-${idx}`}
          className={graphStyleItemClass}
          onClick={() => setCurrentStyle(section.style)}
          data-test-id={section.testId}
        >
          <span>{section.itemContent}</span>
        </li>
      );
    });

  const getGraphSettingsForm = () => {
    const sectionEntry = sections.find((section) => section.style === currentStyle);
    return sectionEntry?.settings ? sectionEntry.settings : <div>No settings available.</div>;
  };

  const submitButton = (
    <Button
      variant={ButtonVariant.PRIMARY}
      onClick={() => onSubmit(currentStyle)}
      testId={ELEMENT.graphDisplaySettingsSubmit}
    >
      GO
    </Button>
  );
  const helpLink = (
    <div>
      {helpIcon}
      <a href={route === K.ROUTE.POWER_QUERY ? HELP_ROUTE.HELP_POWER_QUERIES : HELP_ROUTE.HELP_GRAPHS}>
        Help with {route === K.ROUTE.POWER_QUERY ? 'Power Queries' : 'Graphs'}
      </a>
    </div>
  );

  const rightItems = [submitButton];
  const leftItems = [helpLink];
  return (
    <div className="Dropdown__Menu GraphStyleMenu">
      <div className="GraphStyleMenu__Body">
        <ul className="GraphStyleMenu__StyleList">{getGraphStyleList()}</ul>
        <div className="GraphStyleMenu__SettingsForm">
          <div className="GraphStyleMenu__FormHeader">SETTINGS</div>
          {getGraphSettingsForm()}
        </div>
      </div>
      <div className="GraphStyleMenu__Footer">
        <Toolbar leftItems={leftItems} rightItems={rightItems} />
      </div>
    </div>
  );
}

export default GraphStyleMenu;
